import { useState, useRef, useEffect } from "react";
import {
  RiAttachment2,
  RiCloseLine,
  RiInformationLine,
  RiEditLine,
  RiSaveLine,
} from "react-icons/ri";
import { HiPlus } from "react-icons/hi";
import { TbCloudUpload } from "react-icons/tb";
import { useGlobalStore } from "~/stores/store-provider";
import { FileAttachment } from "~/stores/slices/connection-attachments";
import DeleteConfirmationModal from "./delete-confirmation-modal";

interface FileUploadCardProps {
  // No props needed - using global state
}

const FileUploadCard = ({}: FileUploadCardProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get attachments state and functions from global store
  const {
    attachments,
    addAttachments,
    retryUpload,
    initiateDelete,
    pendingDelete,
    editAttachment,
  } = useGlobalStore((state) => state);

  const attachmentsRef = useRef<FileAttachment[]>(attachments);

  // Keep ref in sync with store
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  const handleRetryUpload = async (fileId: string) => {
    await retryUpload(fileId);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addAttachments(event.target.files);
    }
    // Clear the input to allow selecting the same file again
    event.target.value = "";
  };

  const handleFileDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleFileDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);

    if (event.dataTransfer.files) {
      addAttachments(event.dataTransfer.files);
    }
  };

  const removeFile = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    event.stopPropagation();
    const attachmentToDelete = attachments[index];
    if (attachmentToDelete) {
      // Initiate delete - this will show the confirmation modal
      initiateDelete(attachmentToDelete);
    }
  };

  const handleEditToggle = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    event.stopPropagation();
    editAttachment(index, { isEditing: !attachments[index].isEditing });
  };

  const handleDescriptionChange = (index: number, description: string) => {
    editAttachment(index, { description });
  };

  return (
    <div className="card bg-base-100 mt-3">
      <div
        className={`card-body flex flex-col gap-3 border-2 border-dashed p-3 transition-all duration-200 cursor-pointer ${
          isDragOver
            ? "border-primary bg-primary/10"
            : "border-base-content/30 hover:border-primary hover:bg-primary/5"
        }`}
        onDragOver={handleFileDragOver}
        onDragLeave={handleFileDragLeave}
        onDrop={handleFileDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="text-sm font-medium">
          <div className="flex items-center gap-1">
            <RiAttachment2 />
            Upload Files to Understand the Database
            <div
              className="tooltip tooltip-bottom"
              data-tip="Attach files like documentation, sample queries, schema descriptions, etc. which can help the AI analyze your data better"
            >
              <RiInformationLine className="text-gray-500" />
            </div>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,.md,.docx,.pdf"
          multiple
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
        {attachments.length ? (
          <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
            {attachments.map((attachment: FileAttachment, index: number) => (
              <div key={index} className="flex flex-col">
                <div className="flex justify-end">
                  <button
                    className={`btn btn-xs btn-circle btn-ghost mb-1 ${
                      attachment.isEditing ? "btn-primary" : "btn-info"
                    } hover:text-white`}
                    onClick={(e) => handleEditToggle(e, index)}
                  >
                    {attachment.isEditing ? (
                      <RiSaveLine size={12} />
                    ) : (
                      <RiEditLine size={12} />
                    )}
                  </button>
                  <button
                    className="btn btn-xs btn-circle btn-ghost btn-error"
                    onClick={(event) => removeFile(event, index)}
                  >
                    <RiCloseLine size={14} />
                  </button>
                </div>
                <div className="relative flex gap-1">
                  <div className="relative flex items-center justify-center w-32 h-20 bg-base-300 rounded">
                    {attachment.isUploading && (
                      <span className="absolute top-1 right-1 loading loading-spinner loading-xs"></span>
                    )}
                    {attachment.isUploadError && (
                      <button
                        className="absolute top-1 btn btn-circle btn-sm btn-ghost btn-primary"
                        onClick={() => {
                          handleRetryUpload(attachment.fileId);
                        }}
                      >
                        <TbCloudUpload />
                      </button>
                    )}
                    <span className="text-xs text-center truncate max-w-20 px-1">
                      {attachment.fileName}
                    </span>
                  </div>
                  <textarea
                    disabled={!attachment.isEditing}
                    placeholder="Add a short note about this file (optional)"
                    value={attachment.description}
                    onChange={(e) => {
                      if (attachment.isEditing) {
                        handleDescriptionChange(index, e.target.value);
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="textarea textarea-md ml-1 w-full textarea-accent"
                  ></textarea>
                </div>
              </div>
            ))}
            <div className="text-gray-500 p-2 flex justify-center">
              <HiPlus />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1 items-center">
            <div className="text-gray-500 p-1">
              <HiPlus />
            </div>
            <p className="text-xs text-gray-500">
              {isDragOver ? "Drop File here" : "Add or Drop File"}
            </p>
          </div>
        )}

        <p className="text-xs text-gray-500">
          Accepted file types: .txt, .md, .docx, .pdf
        </p>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={!!pendingDelete}
        fileName={pendingDelete?.file.name || ""}
      />
    </div>
  );
};

export default FileUploadCard;
